import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * @typedef {Object} JarSelectorRefs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement[] | HTMLButtonElement | undefined} jarButton
 *
 * @extends {Component<JarSelectorRefs>}
 */
class JarSelectorComponent extends Component {
  requiredRefs = ['quantityInput'];

  connectedCallback() {
    super.connectedCallback();
    this.#updateButtonStates();
    this.#syncSelectedButton();
  }

  /**
   * Handles jar button selection.
   * @param {MouseEvent} event
   */
  selectJar(event) {
    event.preventDefault();

    if (!(event.target instanceof HTMLElement)) return;

    const button = event.target.closest('button[data-jar-quantity]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    this.#setQuantity(this.#parseQuantity(button.dataset.jarQuantity), true);
  }

  /**
   * @returns {string}
   */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * @param {string} value
   */
  setValue(value) {
    this.#setQuantity(this.#parseQuantity(value), false);
  }

  /**
   * @param {number} cartQty
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.updateCartQuantity();
  }

  /**
   * @param {string} min
   * @param {string | null} max
   * @param {string} step
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;

    quantityInput.min = min;
    quantityInput.step = step;

    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }

    this.updateCartQuantity();
  }

  updateCartQuantity() {
    this.#setQuantity(this.#parseQuantity(this.refs.quantityInput.value), false);
  }

  /**
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}}
   */
  canAddToCart() {
    const { max, cartQuantity, value } = this.#getCurrentValues();
    const wouldExceedMax = max !== null && cartQuantity + value > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd: value,
    };
  }

  /**
   * @param {number} quantity
   * @param {boolean} shouldDispatch
   */
  #setQuantity(quantity, shouldDispatch) {
    const clampedQuantity = this.#clampToRules(quantity);

    this.refs.quantityInput.value = clampedQuantity.toString();
    this.#updateButtonStates();
    this.#syncSelectedButton();

    if (shouldDispatch) {
      this.dispatchEvent(new QuantitySelectorUpdateEvent(clampedQuantity));
    }
  }

  /**
   * @param {number} quantity
   * @returns {number}
   */
  #clampToRules(quantity) {
    const { min, step } = this.#getCurrentValues();
    const effectiveMax = this.#getEffectiveMax();
    const safeQuantity = Number.isFinite(quantity) ? quantity : min;
    const steppedQuantity = min + Math.round((safeQuantity - min) / step) * step;

    return Math.min(effectiveMax ?? Infinity, Math.max(min, steppedQuantity));
  }

  #updateButtonStates() {
    const { min, step } = this.#getCurrentValues();
    const effectiveMax = this.#getEffectiveMax();
    const disabled = this.refs.quantityInput.disabled;

    for (const button of this.#getButtons()) {
      const quantity = this.#parseQuantity(button.dataset.jarQuantity);
      const validIncrement = (quantity - min) % step === 0;
      const validMaximum = effectiveMax === null || quantity <= effectiveMax;

      button.disabled = disabled || quantity < min || !validIncrement || !validMaximum;
    }
  }

  #syncSelectedButton() {
    const selectedQuantity = this.#parseQuantity(this.refs.quantityInput.value).toString();

    for (const button of this.#getButtons()) {
      const isSelected = button.dataset.jarQuantity === selectedQuantity;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    }
  }

  /**
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  #getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: this.#parseQuantity(quantityInput.min || quantityInput.dataset.min || '1'),
      max: quantityInput.max ? this.#parseQuantity(quantityInput.max) : null,
      step: this.#parseQuantity(quantityInput.step || '1'),
      value: this.#parseQuantity(quantityInput.value),
      cartQuantity: this.#parseQuantity(quantityInput.getAttribute('data-cart-quantity')),
    };
  }

  /**
   * @returns {number | null}
   */
  #getEffectiveMax() {
    const { max, cartQuantity, min } = this.#getCurrentValues();

    if (max === null) return null;

    return Math.max(max - cartQuantity, min);
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  #getButtons() {
    const { jarButton } = this.refs;

    if (!jarButton) return [];

    return Array.isArray(jarButton) ? jarButton : [jarButton];
  }

  /**
   * @param {string | number | null | undefined} value
   * @returns {number}
   */
  #parseQuantity(value) {
    const parsed = Number.parseInt(value?.toString() || '', 10);

    return Number.isFinite(parsed) ? parsed : 1;
  }
}

if (!customElements.get('jar-selector-component')) {
  customElements.define('jar-selector-component', JarSelectorComponent);
}
